import React, { useEffect, useState } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus, PublicClientApplication } from "@azure/msal-browser";
import { RETURN_URL_STORAGE_KEY } from "./config/authConfig";
import './App.css';
import { Navigation } from './components/Navbar';
import { Footer } from './components/Footer';
import { AppRouter } from './router/AppRouter';
import { Sidebar } from './components/Sidebar';
import { UserProfileProvider, useUserProfileContext } from './context/UserProfileContext';

function AuthenticationHandler({ onSessionExpired }: { onSessionExpired: () => void }) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // After login, redirect to the saved returnUrl or dashboard
    if (isAuthenticated && window.location.pathname === '/') {
      const stored = sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
      sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
      const returnUrl = (location.state as any)?.returnUrl || stored;
      navigate(returnUrl || '/dashboard');
    }
  }, [isAuthenticated, navigate, location.state]);

  useEffect(() => {
    // Detect the session-expired scenario:
    // - MSAL has finished all interactions (no redirect in progress)
    // - User is not authenticated (no account in cache)
    // - But we previously set the reauth-attempted flag (meaning we already
    //   tried a redirect and it didn't recover the session)
    // This is exactly the state that used to loop — instead, we show the
    // SessionExpiredScreen once and stop all further redirects.
    if (
      inProgress === InteractionStatus.None &&
      !isAuthenticated &&
      sessionStorage.getItem('connect.reauth.attempted') === 'true'
    ) {
      onSessionExpired();
    }
  }, [inProgress, isAuthenticated, onSessionExpired]);

  return null;
}

function AccessRestrictedPage() {
  const { instance } = useMsal();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 max-w-lg w-full p-10 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m5-7a3 3 0 11-6 0 3 3 0 016 0zm-3 7a7 7 0 100-14 7 7 0 000 14z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-3">Access Restricted</h1>
        <p className="text-slate-600 mb-6 leading-relaxed">
          This environment is limited to authorized testers only. 
          Please use the production version of Connect.
        </p>
        <div className="space-y-3">
          <a 
            href="https://connect.centific.com"
            className="inline-block w-full bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to connect.centific.com
          </a>
          <button
            onClick={() => instance.logoutRedirect()}
            className="inline-block w-full bg-slate-100 text-slate-700 font-medium py-3 px-6 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

function AppLayout() {
  const isAuthenticated = useIsAuthenticated();
  const { accessRestricted } = useUserProfileContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar by default on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isAuthenticated && accessRestricted) {
    return <AccessRestrictedPage />;
  }

  return (
    <div className="app-container min-h-screen flex flex-col">
      <Navigation
        isMobileMenuOpen={sidebarOpen}
        onMobileMenuToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      {isAuthenticated && (
        <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      )}
      <main 
        className={`flex-1 transition-all duration-300 ${
          isAuthenticated ? (sidebarOpen ? 'lg:ml-64' : 'lg:ml-16') : ''
        }`}
      >
        <AppRouter />
      </main>
      <Footer />
    </div>
  );
}

function App({ msalInstance }: { msalInstance: PublicClientApplication }) {
  return (
    <MsalProvider instance={msalInstance}>
      <UserProfileProvider>
        <BrowserRouter>
          <AuthenticationHandler onSessionExpired={() => {}} />
          <AppLayout />
        </BrowserRouter>
      </UserProfileProvider>
    </MsalProvider>
  );
}

export default App;
